/* eslint-disable @nx/enforce-module-boundaries */
import {
  ComposedConditionSet,
  ConditionSet,
  ElementType,
  ListType,
  PageType,
  QuestionAnswerObject,
  QuestionType,
  SubmissionInfo,
  SubmissionTypes,
  SurveyAnyPage,
  SurveyEndPage,
  SurveyListOption,
  SurveyPage,
} from '@hyper-inferno/survey-elements-core';
import {
  BaseComposedPage,
  ComposedElementBuilder,
  ComposedPageBuilder,
} from '@hyper-inferno/survey-elements-core-utils';
import { SurveySession } from '../survey-session/survey-session.class';
import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { ISurveySession } from '../survey-session/survey-session.interface';
import { ListInstructionProcessor } from '../utils/constructed-list-builder';
import { getRandomizedOptions } from '../utils/list-utils';
import { UniversalPiping } from '../utils/universal-piping';

export interface TransformationContextIntilialization
  extends Omit<ITransformationContext, 'listBuilding'> {
  listBuilding: {
    getOptionsByListId?: ITransformationContext['listBuilding']['getOptionsByListId'];
  };
}

// Shared logic for both Node and Angular
export interface ITransformationContext {
  bundle: SurveyBundle;
  session: SurveySession;
  handlers: Record<string, QuestionHandler<any>>;
  piping: {
    transform: (
      text: string,
      ctx: ITransformationContext,
    ) => Promise<string> | string;
  }; // Other services like Translation, etc.
  listBuilding: {
    getOptionsByListId: (
      listId: string,
      ctx: ITransformationContext,
      randomizeOptions: boolean,
    ) => SurveyListOption[];
  };
  /**
   * Persists the session data.
   * @returns A promise resolving to true if successful, false otherwise.
   */
  sessionSave: (
    sessionData: Omit<
      ISurveySession,
      'updated_at' | 'sync_status' | 'last_sync_time'
    >,
  ) => Promise<boolean> | boolean;
}

export interface QuestionHandler<T> {
  buildConfig(el: any, ctx: ITransformationContext): Promise<T> | T;

  getValue?(
    el: any,
    responseData: QuestionAnswerObject,
  ): string | number | number[];
}

export class SurveyBotEngine {
  nextPage(): SurveyAnyPage {
    const currentPage = this.context.session.current_page_id;
    if (currentPage) return this.context.bundle.fetchNextPageQuery(currentPage);
    else return this.context.bundle.fetchFirstPageQuery();
  }
  async saveState() {
    const sessionData = this.context.session.toPersistence();
    return this.context.sessionSave(sessionData);
  }
  askPage() {
    throw new Error('Method not implemented.');
  }
  private context: ITransformationContext;

  constructor(private Icontext: TransformationContextIntilialization) {
    // 1. Default List Building if not provided
    if (!Icontext.listBuilding.getOptionsByListId) {
      Icontext.listBuilding.getOptionsByListId =
        this.getOptionsByListId.bind(this);
    }

    this.context = Icontext as ITransformationContext;
  }

  private async handleLogicPage(
    rawPage: SurveyPage,
  ): Promise<BaseComposedPage> {
    // 1. Get all logic elements and sort them by rank (ascending)
    const logicElements = rawPage.elements
      .filter((el) => el.type === QuestionType.SKIP_LOGIC)
      .sort((a, b) => a.rank - b.rank);

    const handler = this.context.handlers[QuestionType.SKIP_LOGIC] as any;
    // as SkipLogicHandler

    if (handler) {
      // 2. Iterate through each skip logic element in order
      for (const el of logicElements) {
        const nextId = await handler.getNextPageId(
          el,
          this.context,
          this.context.handlers,
        );

        // 3. The first one to return a valid page ID "wins" the jump
        if (nextId) {
          return this.renderPage(nextId);
        }
      }
    }

    // FALLBACK: If no logic elements matched (or none exist), proceed to the next page
    const nextChronologicalPage = this.context.bundle.fetchNextPageQuery(
      rawPage.id,
    );

    if (!nextChronologicalPage) {
      return this.renderPage(this.context.bundle.getFirstEnding().id);
    }

    return this.renderPage(nextChronologicalPage.id);
  }

  private async handleElementPage(rawPage: SurveyPage) {
    const elementPromises = (rawPage.elements ?? []).map(async (el) => {
      const handler = this.context.handlers[el.type];
      // 1. Build the base element properties
      const elementBuilder = new ComposedElementBuilder({
        name: el.name,
        type: el.type,
        // question_text: await this.context.piping.transform(
        //   el.question_text,
        //   this.context,
        // ),
        question_text: new UniversalPiping().transform(
          el.question_text,
          this.context,
        ),
        id: el.id,
        is_skippable: !el.required,
        element_type: ElementType.QUESTION_ELEMENT,
        page_id: rawPage.id,
        rank: el.rank,
        required: el.required ?? true,
      });

      // 2. Use Handler to build the complex config
      if (handler) {
        const config = await handler.buildConfig(el, this.context);
        elementBuilder.setQuestionConfig(config);
      }

      const conditonalDisplay = this.context.bundle.getConditionSetsByElementId(
        String(el.id),
      );

      if (conditonalDisplay) {
        const composedConditionSet = this.buildComposedLogic(
          conditonalDisplay,
          this.context,
        );
        elementBuilder.addConditionalDisplayMeta(composedConditionSet);
      }

      return elementBuilder.build();
    });

    const elements = await Promise.all(elementPromises);

    // 3. Handle Ordering Logic
    // if (rawPage.randomise) {
    // eslint-disable-next-line no-constant-condition
    if (rawPage.randomize_elements) {
      // Fisher-Yates Shuffle
      for (let i = elements.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [elements[i], elements[j]] = [elements[j], elements[i]];
      }
    } else {
      // Sort by rank (Ascending)
      elements.sort((a, b) => a.rank - b.rank);
    }

    const progress = this.context.bundle.getProgressPercentage(rawPage.id);

    this.context.session.moveToQuestion(rawPage.id);

    return new ComposedPageBuilder(rawPage)
      .setElements(elements)
      .setProgress(progress)
      .setPageNavigation(false, false)
      .build();
  }

  async renderPage(pageId: number) {
    const rawPage = this.context.bundle.fetchPageById(pageId);

    if (!rawPage) {
      throw new Error('Page Not Found');
    }
    switch (rawPage.type) {
      case PageType.LOGIC_PAGE_MODEL:
        return this.handleLogicPage(rawPage);
      case PageType.ENDING_PAGE_MODEL:
        return this.handleEndPage(rawPage as unknown as SurveyEndPage);
      case PageType.ELEMENT_PAGE_MODEL:
      default:
        return this.handleElementPage(rawPage);
    }
  }
  handleEndPage(rawPage: SurveyEndPage) {
    this.context.session.moveToQuestion(rawPage.id);
    this.context.session.endSurvey(rawPage.ending_config.ending_status);
    // Check both the boolean flag and if a URL actually exists
    const isRedirecting =
      rawPage.ending_config.has_redirect &&
      rawPage.ending_config.ending_redirect_url;

    const submissionType = isRedirecting
      ? SubmissionTypes.BOT_TERMINATE_SURVEY_WITH_REDIRECT
      : SubmissionTypes.BOT_TERMINATE_SURVEY_NO_REDIRECT;

    const submissionPayload: SubmissionInfo = {
      submissionType,
      submissionPayload: {
        // Pass the URL into the payload so the UI knows where to go
        redirect_url: rawPage.ending_config.ending_redirect_url,
        sys_disposition_code: rawPage.name,
        ending_text: rawPage.ending_config.ending_text ?? 'End of Survey',
      },
      statusChange: rawPage.ending_config.ending_status,
    };

    return new ComposedPageBuilder(rawPage)
      .setPageNavigation(false, false)
      .setSubmissionInfo(submissionPayload)
      .build();
  }

  private buildComposedLogic(
    conditionSet: ConditionSet,
    context: ITransformationContext,
  ): ComposedConditionSet {
    const dependencies: Record<number, any> = {};

    const composedConditions = (conditionSet.conditions ?? []).map(
      (condition) => {
        const elementId = condition.condition_element_id;

        // 1. Resolve Question Type (Needed for the client to know WHICH handler to use, if any)
        const targetElement = context.bundle.fetchElementById(elementId);
        const elementType = targetElement?.type ?? 'unknown';

        if (!targetElement) {
          throw new Error('Target Element is not availabe');
        }

        // 2. Resolve the Option Key (CRITICAL: Client cannot fetch options from bundle)
        let resolvedOptionKey = 0;
        if (condition.option_id) {
          const option = context.bundle.fetchOptionById(condition.option_id);
          resolvedOptionKey = option?.option_key ?? 0;
        }

        // 3. Pluck user value (The dependency)
        // We use the handler's getValue to ensure the format is consistent
        // (e.g. converting numeric strings to actual numbers or handling complex types)
        const handler = this.context.handlers[elementType];
        const rawValue =
          handler && typeof handler.getValue === 'function'
            ? handler.getValue(
                targetElement,
                context.session.jsn_responses[elementId] ?? {},
              )
            : context.session.jsn_responses[elementId];

        // 4. Update the dependency map
        dependencies[elementId] = rawValue ?? null;

        return {
          ...condition,
          element_type: elementType, // Tell client what kind of question this is
          option_key: resolvedOptionKey, // Pre-resolved comparison value
          element: targetElement,
        };
      },
    );

    return {
      ...conditionSet,
      conditions: composedConditions,
      dependencies, // This allows the client to evaluate without a global state
    };
  }

  getFirstPage() {
    return this.context.bundle.fetchFirstPageQuery();
  }

  private getOptionsByListId(
    listId: string,
    ctx: ITransformationContext,
    randomize: boolean,
  ): SurveyListOption[] {
    const list = ctx.bundle.getListByListIdQuery(listId);

    if (list?.list_type == ListType.NORMAL_LIST) {
      const options = ctx.bundle.getListByListIdQuery(listId)?.options ?? [];

      if (!options) return [];

      if (randomize) {
        return getRandomizedOptions(options);
      } else {
        return options;
      }
    } else {
      return ListInstructionProcessor.process(listId, ctx);
    }
  }
}
